import { Clock } from "lucide-react";

type ComingSoonProps = {
  /** Admin-authored message explaining what's coming. Falsy → a friendly default. */
  message?: string | null;
  /** Optional heading override (defaults to "Coming soon"). */
  title?: string;
  /** Stable suffix for test ids so multiple overlays on a page stay unique. */
  testidSuffix?: string;
};

const DEFAULT_MESSAGE =
  "We're putting the finishing touches on this feature. Check back soon — it's on the way!";

/**
 * Friendly "Coming Soon" empty state shown when a feature is gated in
 * `coming_soon` mode. The nav link stays visible; landing here shows the
 * admin-authored message. Uses brand theme tokens (not raw hex) so it adapts to
 * dark mode automatically.
 */
export function ComingSoon({ message, title = "Coming soon", testidSuffix }: ComingSoonProps) {
  const suffix = testidSuffix ? `-${testidSuffix}` : "";
  return (
    <div
      className="flex flex-col items-center justify-center py-24 px-6 text-center"
      data-testid={`coming-soon${suffix}`}
    >
      <div className="rounded-full bg-brand-muted p-5 mb-4">
        <Clock className="h-8 w-8 text-brand" />
      </div>
      <h2
        className="text-xl font-semibold text-foreground mb-2"
        data-testid={`coming-soon-title${suffix}`}
      >
        {title}
      </h2>
      <p
        className="text-sm text-muted-foreground max-w-md whitespace-pre-line"
        data-testid={`coming-soon-message${suffix}`}
      >
        {message?.trim() ? message : DEFAULT_MESSAGE}
      </p>
    </div>
  );
}

export default ComingSoon;
